import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { isMobile, browserName, deviceType } from 'react-device-detect';
import i18n from 'i18next';
import { mockApi } from '../../api/mockApi';
import * as storeApi from '../../api/storeApi';
import { savedCardAllowedForThisBooking } from '../../Pages/AddApointment/participants';
import { getVisitorKey } from '../../utils/visitorKey';
import { isDomainMode } from '../../utils/domainMode';
import { resolveShownDuration } from '../../Pages/AddApointment/bookingTotals';

/**
 * Get current locale for OTP/notifications: the reader's OWN choice when they
 * made one (`languageExplicit`), otherwise the language the page is shown in.
 * The stored `language` alone is not a choice -- index.js writes the device
 * default into it -- and on a shop's page the page is in the shop's language
 * (utils/shopLanguage), so reading storage first sent an English phone reading
 * a Hebrew shop its login code in English.
 */
function getCurrentLocale() {
  let stored = null;
  let explicit = false;
  try {
    stored = typeof localStorage !== 'undefined' ? localStorage.getItem('language') : null;
    explicit = typeof localStorage !== 'undefined' && localStorage.getItem('languageExplicit') === '1';
  } catch (_) { /* storage refused */ }
  const raw = (explicit && stored) || i18n?.language || stored || 'en';
  return (raw || 'en').split('-')[0];
}

// API base URL (same as Config/Api) – for components that build image/request URLs
const getApiBaseUrl = () => {
  // Always use environment variable if set (production builds will have this)
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  
  // Development fallback: detect if opened from another device for local testing
  const host = typeof window !== 'undefined' && window.location?.hostname;
  const isLocalhost = !host || host === 'localhost' || host === '127.0.0.1';
  const isPrivateIP = host && /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host);
  
  if (!isLocalhost && isPrivateIP) {
    return `http://${host}:3000/v1`;
  }
  return 'http://localhost:3000/v1';
};
export const BASE_URL = getApiBaseUrl();
export const BASE_HOST = BASE_URL.replace(/\/v1\/?$/, '');

/**
 * Build categoriesWithServices from raw API categories + services arrays.
 * The server filters out inactive categories before sending them, so every category
 * in the input array is considered active/visible.
 * Exported for unit testing.
 */
export function buildCategoriesWithServices(categories, services) {
  if (!Array.isArray(categories) || !Array.isArray(services)) return [];
  const knownCategoryIds = new Set(categories.map((cat) => cat.id ?? cat._id).filter((id) => id != null));
  const uncategorized = services
    .filter((s) => {
      const catId = s.categoryId ?? s.category_id;
      return catId == null || !knownCategoryIds.has(catId);
    })
    .map((s) => ({ ...s, _id: s._id || s.id }));
  const categorized = categories.map((cat) => {
    const catId = cat.id ?? cat._id;
    return {
      _id: catId,
      name: cat.name,
      translations: cat.translations || {},
      services: services
        .filter((s) => (s.categoryId ?? s.category_id) === catId)
        .map((s) => ({ ...s, _id: s._id || s.id })),
    };
  });
  if (uncategorized.length > 0) {
    return [{ _id: null, name: null, translations: {} , services: uncategorized }, ...categorized];
  }
  return categorized;
}

/** Normalize campaigns for store (main page + booking flow): consistent id and showInTop. */
export function normalizeCampaigns(rawCampaigns) {
  const arr = Array.isArray(rawCampaigns) ? rawCampaigns : [];
  return arr.map((c) => ({
    ...c,
    id: c.id ?? c._id,
    _id: c._id ?? c.id,
    showInTop: c.showInTop ?? c.showAtTop ?? c.pinned ?? false,
  }));
}

/** Normalize phone to E.164. Uses parsePhoneInput when phone may include country code; otherwise uses salon country. */
function normalizePhoneWithCountryCode(phone, salonCountryCode) {
  const parsed = parsePhoneInput(phone, salonCountryCode);
  if (parsed.ok && parsed.e164) return parsed.e164;
  const digits = String(phone || '').replace(/\D/g, '').trim();
  if (!digits) return '';
  const code = String(salonCountryCode || '972').replace(/\D/g, '');
  let national = digits.startsWith(code)
    ? digits.slice(code.length).replace(/^0+/, '') || digits.slice(code.length)
    : digits.replace(/^0+/, '') || digits;
  return `+${code}${national}`;
}

function getSalonCountryCode(salon) {
  const raw = salon?.countryCode ?? salon?.countryCodeNumber ?? salon?.country;
  if (raw == null) return '972';
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) return s;
  const map = { IL: '972', IE: '353', US: '1', GB: '44', HE: '972' };
  return map[s.toUpperCase()] ?? '972';
}

/** Known country calling codes (digits only) and their length for parsing. Order matters: longer first. */
const COUNTRY_CODES = [
  { code: '972', len: 3 },  // Israel
  { code: '353', len: 3 },  // Ireland
  { code: '44', len: 2 },   // UK
  { code: '33', len: 2 },   // France
  { code: '49', len: 2 },   // Germany
  { code: '39', len: 2 },   // Italy
  { code: '81', len: 2 },   // Japan
  { code: '1', len: 1 },    // US/Canada
];

/**
 * Parse phone input and extract country code + national number.
 * Supports: 0526123456, +972526123456, +9720526123456, +972 52-612-3456.
 * When user provides a country code (e.g. +972), that is used; otherwise uses fallbackCountryCode (e.g. salon's).
 * @returns {{ ok: boolean, e164?: string, countryCode?: string, nationalNumber?: string, errorMessage?: string }}
 */
export function parsePhoneInput(rawInput, fallbackCountryCode) {
  const digits = String(rawInput || '').replace(/\D/g, '').trim();
  const fallback = String(fallbackCountryCode || '972').replace(/\D/g, '') || '972';

  if (!digits) {
    return { ok: false, errorMessage: 'error_phone_missing' };
  }
  if (digits.length > 15) {
    return { ok: false, errorMessage: 'error_phone_invalid' };
  }
  if (digits.length < 9) {
    return { ok: false, errorMessage: 'error_phone_invalid' };
  }

  let countryCode = fallback;
  let nationalNumber = digits;

  if (digits.startsWith('0')) {
    nationalNumber = digits.replace(/^0+/, '') || digits;
    if (nationalNumber.length < 8 || nationalNumber.length > 11) {
      return { ok: false, errorMessage: 'error_phone_invalid' };
    }
    countryCode = fallback;
  } else {
    const matched = COUNTRY_CODES.find(({ code }) => digits.startsWith(code));
    if (matched) {
      const rest = digits.slice(matched.len).replace(/^0+/, '') || digits.slice(matched.len);
      if (rest.length >= 8 && rest.length <= 11 && (matched.len + rest.length) <= 15) {
        countryCode = matched.code;
        nationalNumber = rest;
      }
    }
    if (nationalNumber === digits) {
      nationalNumber = digits.replace(/^0+/, '') || digits;
      if (nationalNumber.length < 8 || nationalNumber.length > 11) {
        return { ok: false, errorMessage: 'error_phone_invalid' };
      }
      countryCode = fallback;
    }
  }

  const e164 = `+${countryCode}${nationalNumber}`;
  if (e164.length > 16) {
    return { ok: false, errorMessage: 'error_phone_invalid' };
  }
  return { ok: true, e164, countryCode, nationalNumber };
}

/**
 * Build full image URL for uploads (gallery, logos, etc.).
 * Always uses BASE_HOST so images load from the same server as the API.
 * Rewrites stored URLs from other origins (e.g. backend on 5000) to the configured API origin.
 */
export function toImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  try {
    const path = url.startsWith('http://') || url.startsWith('https://')
      ? new URL(url).pathname
      : (url.startsWith('/') ? url : `/${url}`);
    if (!path.startsWith('/uploads')) return url.startsWith('http') ? url : `${BASE_HOST}${path}`;
    return `${BASE_HOST}${path}`;
  } catch {
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${BASE_HOST}${path}`;
  }
}

// Use real Stylon server when REACT_APP_USE_MOCK is not set to 'true'
const isMockMode = () => process.env.REACT_APP_USE_MOCK === 'true';

const getSalonInfo = () => {
  return async (dispatch) => {
    try {
      if (isMockMode()) {
        const mockSalon = {};
        dispatch({ type: "GET_SALON_INFO", payload: mockSalon });
        return;
      }
      // Real API: list salons not used by store (store uses slug from URL). No-op or fetch from config if needed.
      dispatch({ type: "GET_SALON_INFO", payload: {} });
    } catch (error) {
      console.log("error == >", error);
    }
  };
};

const getSalonSpecificInfo = (_id, language = "English") => {
  return async (dispatch) => {
    // Marks a salon fetch as in flight so <SplashGate/> knows to hold the boot
    // splash until it resolves. Dispatched synchronously, before any await, so
    // it has landed by the time the gate checks one frame after mount.
    dispatch({ type: "GET_SALON_INFO_START" });
    // Custom-domain (single-tenant) mode: the salon is identified by the HOST,
    // not by a URL slug. In domain mode there is no slug segment, so callers
    // fall back to sessionStorage or the hostname and would otherwise hit
    // /config/salon-by-slug/<host> → 404. Resolve by host here so EVERY
    // salon-scoped page (booking, gallery, packages, booking-successful, …)
    // works, without touching each caller. Platform mode is unchanged.
    if (isDomainMode()) {
      return dispatch(getSalonSpecificInfoByHost(window.location.hostname, language));
    }
    try {
      if (isMockMode()) {
        // Return a realistic mock salon object for the given username
        const mockSalon = {
          _id,
          name: `Mock Salon for ${_id}`,
          username: _id,
          city: 'Mock City',
          address: '123 Mock St',
          location: { type: 'Point', coordinates: [34.7818, 32.0853] },
          aboutDescription: 'We are a modern salon offering a wide range of beauty and grooming services. Our team of professionals is dedicated to providing the best experience for our clients. Mock Salon is your go-to place for hair, beauty, and relaxation. Enjoy our premium packages and expert staff.',
          slogan: 'Your Beauty, Our Passion',
          tagline: 'Your Beauty, Our Passion',
          contactNumber: '+123 456 7894',
          instagram: 'mock_salon_insta',
          logo: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=facearea&w=400&h=250',
          coverPhoto: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=facearea&w=1200&h=400',
          specialists: [
            { _id: 'spec-1', name: 'Sarah', designation: 'Stylist', image: 'https://randomuser.me/api/portraits/women/1.jpg', servicesIds: ['svc-1', 'svc-2'] },
            { _id: 'spec-2', name: 'Mike', designation: 'Barber', image: 'https://randomuser.me/api/portraits/men/2.jpg', servicesIds: ['svc-1', 'svc-2'] },
            { _id: 'spec-3', name: 'Anna', designation: 'Colorist', image: 'https://randomuser.me/api/portraits/women/3.jpg', servicesIds: ['svc-1', 'svc-2', 'svc-3'] },
          ],
          categoriesWithServices: [
            {
              _id: 'cat-1',
              name: 'Hair',
              services: [
                { _id: 'svc-1', name: 'Haircut', nameEnglish: 'Haircut', duration: 1800, durationMin: 30, price: 80, categoryId: 'cat-1' },
                { _id: 'svc-2', name: 'Hair Dye', nameEnglish: 'Hair Dye', duration: 3600, durationMin: 60, price: 120, categoryId: 'cat-1' },
                { _id: 'svc-3', name: 'Styling', nameEnglish: 'Styling', duration: 1800, durationMin: 30, price: 50, categoryId: 'cat-1' },
              ],
            },
          ],
          openingDaysHours: (() => {
            const days = [
              'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
            ];
            const todayIdx = new Date().getDay();
            const now = new Date();
            const pad = n => n.toString().padStart(2, '0');
            // Today is currently open: window is 1 hour before now to 1 hour after now
            const startHour = now.getHours() === 0 ? 0 : now.getHours() - 1;
            const endHour = now.getHours() === 23 ? 23 : now.getHours() + 1;
            const openNowStart = pad(startHour) + ':00:00';
            const openNowEnd = pad(endHour) + ':59:59';
            return days.map((day, idx) => {
              if (idx === todayIdx) {
                return {
                  day,
                  openingWindows: [
                    { startTime: openNowStart, endTime: openNowEnd },
                  ],
                };
              }
              if (day === 'Monday') {
                return {
                  day: 'Monday',
                  openingWindows: [
                    { startTime: '09:00:00', endTime: '12:00:00' },
                    { startTime: '14:00:00', endTime: '18:00:00' },
                  ],
                };
              }
              if (day === 'Friday') {
                return {
                  day: 'Friday',
                  openingWindows: [
                    { startTime: '09:00:00', endTime: '15:00:00' },
                  ],
                };
              }
              return {
                day,
                openingWindows: [],
              };
            });
          })(),
          packages: [
            { _id: 'pkg-1', name: 'Basic Package', price: 100, duration: 3600, durationMin: 60, coverPhoto: 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=facearea&w=400&h=250', services: [{ name: 'Haircut', durationMin: 30 }, { name: 'Styling', durationMin: 30 }] },
            { _id: 'pkg-2', name: 'Premium Package', price: 180, duration: 7200, durationMin: 120, coverPhoto: 'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?auto=format&fit=facearea&w=400&h=250', services: [{ name: 'Haircut', durationMin: 30 }, { name: 'Hair Dye', durationMin: 60 }, { name: 'Styling', durationMin: 30 }] },
          ],
          ratings: [
            { user: 'Alice', rating: 5, comment: 'Great service!' },
            { user: 'Bob', rating: 4, comment: 'Very good.' },
            { user: 'Carol', rating: 5, comment: 'Amazing experience.' },
          ],
          averageRating: 4.7,
        };
        dispatch({
          type: "GET_SALON_INFO",
          payload: mockSalon,
        });
        return 200;
      }
      const customerId = typeof localStorage !== 'undefined' ? localStorage.getItem('customerId') || undefined : undefined;
      const data = await storeApi.getSalonBySlug(_id, customerId);
      const payload = data || {};
      // Ensure location.coordinates exists for UI (GeoJSON [lng, lat]). Backend may send location or latitude/longitude.
      if (!payload.location?.coordinates?.length && payload.latitude != null && payload.longitude != null) {
        const lat = Number(payload.latitude);
        const lng = Number(payload.longitude);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          payload.location = { type: 'Point', coordinates: [lng, lat] };
        }
      }
      if (payload.phone != null && payload.contactNumber == null) payload.contactNumber = payload.phone;
      // Map openingHours (API: day 0-6, timeWindows [{start,end}]) → openingDaysHours (UI: day name, openingWindows [{startTime,endTime}])
      if (!payload.openingDaysHours?.length && Array.isArray(payload.openingHours) && payload.openingHours.length > 0) {
        const DAY_NAMES_MAP = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        payload.openingDaysHours = payload.openingHours.map((dh) => ({
          day: DAY_NAMES_MAP[dh.day] || DAY_NAMES_MAP[0],
          openingWindows: (dh.timeWindows || []).map((tw) => ({
            startTime: tw.start ? (tw.start.length === 5 ? tw.start + ':00' : tw.start) : '00:00:00',
            endTime: tw.end ? (tw.end.length === 5 ? tw.end + ':00' : tw.end) : '00:00:00',
          })),
        }));
      }
      // Build categoriesWithServices from API's categories + services (store expects categoriesWithServices)
      if ((!payload.categoriesWithServices || payload.categoriesWithServices.length === 0) && Array.isArray(payload.categories) && Array.isArray(payload.services)) {
        payload.categoriesWithServices = buildCategoriesWithServices(payload.categories, payload.services);
      }
      // Normalize campaigns for store (main page "Discounts & offers"): ensure array and consistent field names
      const rawCampaigns = payload.campaigns ?? payload.activeCampaigns ?? [];
      if (typeof window !== "undefined") {
        console.log("[Campaigns] getSalonBySlug raw from API: hasCampaigns=", !!payload.campaigns, "hasActiveCampaigns=", !!payload.activeCampaigns, "rawCount=", Array.isArray(rawCampaigns) ? rawCampaigns.length : 0, "raw=", rawCampaigns?.slice?.(0, 3)?.map((c) => ({ id: c?.id, _id: c?._id, name: c?.name, showInTop: c?.showInTop, showAtTop: c?.showAtTop, pinned: c?.pinned, bookingDateStart: c?.bookingDateStart, bookingDateEnd: c?.bookingDateEnd })));
      }
      payload.campaigns = rawCampaigns.length > 0 ? normalizeCampaigns(rawCampaigns) : (Array.isArray(payload.campaigns) ? payload.campaigns : []);
      if (typeof window !== "undefined") {
        console.log("[Campaigns] getSalonBySlug after normalize: campaignsCount=", payload.campaigns?.length ?? 0, "list=", payload.campaigns?.map((c) => ({ id: c.id, name: c.name, showInTop: c.showInTop, bookingDateStart: c.bookingDateStart, bookingDateEnd: c.bookingDateEnd })));
      }
      dispatch({
        type: "GET_SALON_INFO",
        payload,
      });
      // The Pixel and the ad landing: started and recorded by storeApi for every page that
      // loads a business — this one included (see noteShopVisit).
      return 200;
    } catch (error) {
      console.log("error == >", error);
      dispatch({ type: "GET_SALON_INFO", payload: {} });
      return error.response?.status ?? 500;
    }
  };
};

// —— Custom-domain variant of getSalonSpecificInfo: resolves the salon by
// hostname instead of slug (single-tenant custom domains). Dispatches into
// the SAME reducer target ("GET_SALON_INFO") so every downstream selector
// that reads state.getSalonReducer.data works unchanged. Mirrors the
// real-API branch of getSalonSpecificInfo (mock mode isn't reachable in
// domain mode, so it's omitted here). ——
const getSalonSpecificInfoByHost = (host, language = "English") => {
  return async (dispatch) => {
    dispatch({ type: "GET_SALON_INFO_START" }); // see getSalonSpecificInfo
    try {
      const customerId = typeof localStorage !== 'undefined' ? localStorage.getItem('customerId') || undefined : undefined;
      const data = await storeApi.getSalonByHost(host, customerId);
      const payload = data || {};
      // Ensure location.coordinates exists for UI (GeoJSON [lng, lat]). Backend may send location or latitude/longitude.
      if (!payload.location?.coordinates?.length && payload.latitude != null && payload.longitude != null) {
        const lat = Number(payload.latitude);
        const lng = Number(payload.longitude);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          payload.location = { type: 'Point', coordinates: [lng, lat] };
        }
      }
      if (payload.phone != null && payload.contactNumber == null) payload.contactNumber = payload.phone;
      // Map openingHours (API: day 0-6, timeWindows [{start,end}]) → openingDaysHours (UI: day name, openingWindows [{startTime,endTime}])
      if (!payload.openingDaysHours?.length && Array.isArray(payload.openingHours) && payload.openingHours.length > 0) {
        const DAY_NAMES_MAP = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        payload.openingDaysHours = payload.openingHours.map((dh) => ({
          day: DAY_NAMES_MAP[dh.day] || DAY_NAMES_MAP[0],
          openingWindows: (dh.timeWindows || []).map((tw) => ({
            startTime: tw.start ? (tw.start.length === 5 ? tw.start + ':00' : tw.start) : '00:00:00',
            endTime: tw.end ? (tw.end.length === 5 ? tw.end + ':00' : tw.end) : '00:00:00',
          })),
        }));
      }
      // Build categoriesWithServices from API's categories + services (store expects categoriesWithServices)
      if ((!payload.categoriesWithServices || payload.categoriesWithServices.length === 0) && Array.isArray(payload.categories) && Array.isArray(payload.services)) {
        payload.categoriesWithServices = buildCategoriesWithServices(payload.categories, payload.services);
      }
      // Normalize campaigns for store (main page "Discounts & offers"): ensure array and consistent field names
      const rawCampaigns = payload.campaigns ?? payload.activeCampaigns ?? [];
      payload.campaigns = rawCampaigns.length > 0 ? normalizeCampaigns(rawCampaigns) : (Array.isArray(payload.campaigns) ? payload.campaigns : []);
      dispatch({
        type: "GET_SALON_INFO",
        payload,
      });
      // Bridge host → real slug: salon-scoped pages/analytics read
      // sessionStorage.salonId as a slug (e.g. viewSalon → /salon-by-slug/<id>).
      // In domain mode there is no URL slug, so stamp the resolved salon's real
      // slug here (NOT the hostname) so those slug-based calls resolve correctly.
      try {
        const realSlug = payload.slug ?? payload.username;
        if (realSlug) sessionStorage.setItem("salonId", realSlug);
      } catch (_e) {}
      // The Pixel and the ad landing: started and recorded by storeApi for every page that
      // loads a business — this one included (see noteShopVisit).
      return 200;
    } catch (error) {
      console.log("error == >", error);
      dispatch({ type: "GET_SALON_INFO", payload: {} });
      return error.response?.status ?? 500;
    }
  };
};

export const mapAppointmentFromApi = (apt) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[SPECIALIST_DEBUG] mapAppointmentFromApi raw appointment', {
      appointmentId: apt?.id ?? apt?._id,
      specialistFromApi: apt?.specialist
        ? {
            id: apt.specialist.id ?? apt.specialist._id,
            displayName: apt.specialist.displayName,
            name: apt.specialist.name,
            userDisplayName: apt.specialist.user?.displayName,
            userName: apt.specialist.user?.name,
          }
        : null,
      salonFromApi: apt?.salon ? { id: apt.salon.id, name: apt.salon.name, slug: apt.salon.slug } : null,
    });
  }
  const specialist = apt.specialist
    ? {
        _id: apt.specialist.id ?? apt.specialist._id,
        id: apt.specialist.id ?? apt.specialist._id,
        name: apt.specialist.displayName || apt.specialist.name || apt.specialist.user?.displayName || apt.specialist.user?.name || '',
        displayName: apt.specialist.displayName || apt.specialist.name || apt.specialist.user?.displayName || apt.specialist.user?.name || '',
        // Their name in each language: the page shows the shopper's own.
        translations: apt.specialist.translations ?? null,
        image: apt.specialist.photoUrl || apt.specialist.imageUrl || apt.specialist.image,
        photoUrl: apt.specialist.photoUrl || apt.specialist.imageUrl || apt.specialist.image,
        appointmentRequestMessage: apt.specialist.appointmentRequestMessage || apt.specialist.message || apt.specialist.welcomeMessage || apt.specialist.bio || null,
      }
    : {};
  if (process.env.NODE_ENV !== 'production' && apt?.specialist) {
    console.log('[SPECIALIST_DEBUG] mapAppointmentFromApi mapped specialist name', {
      appointmentId: apt?.id ?? apt?._id,
      mappedName: specialist.name,
      mappedDisplayName: specialist.displayName,
    });
  }
  const salon = apt.salon
    ? {
        id: apt.salon.id,
        name: apt.salon.name,
        logo: apt.salon.logoUrl ?? apt.salon.logo,
        logoUrl: apt.salon.logoUrl ?? apt.salon.logo,
        address: apt.salon.address,
        username: apt.salon.slug,
        slug: apt.salon.slug,
        // Carry the review toggles through so AppointmentDetails can gate the
        // "Rate your appointment" CTA (enabled) + reframe the composer copy to
        // private feedback when the salon doesn't publish ratings (public).
        // The appointment serializer returns these inside `salon.settings`
        // (JSON); some endpoints flatten them onto the salon — read either.
        appointmentReviewsEnabled:
          apt.salon.appointmentReviewsEnabled ?? apt.salon.settings?.appointmentReviewsEnabled,
        appointmentReviewsPublic:
          apt.salon.appointmentReviewsPublic ?? apt.salon.settings?.appointmentReviewsPublic,
      }
    : {};
  const itemDiscount = (i) => Number(i.discountAmount ?? i.discount_amount ?? 0) || 0;
  // Per-line duration: prefer the appointment ITEM's persisted durationMin (which
  // reflects a portal duration EDIT — editable-duration feature) and only fall back
  // to the service/package catalog default when the item never carried its own value.
  // Without this, an edited appointment would show the catalog duration to the
  // customer and mis-size the calendar/ICS end time (totalDuration below).
  const services = (apt.items || []).filter((i) => i.service).map((i) => ({
    name: i.service.name,
    price: Number(i.service?.price ?? 0),
    duration: i.durationMin ?? i.service?.durationMin ?? 0,
    serviceId: i.serviceId || i.service?.id,
    hidePrice: i.service?.hidePrice ?? false,
    includedInDiscount: itemDiscount(i) > 0,
  }));
  const packages = (apt.items || []).filter((i) => i.package).map((i) => ({
    name: i.package.name,
    price: Number(i.package?.price ?? 0),
    duration: i.durationMin ?? i.package?.durationMin ?? 0,
    packageId: i.packageId || i.package?.id,
    includedInDiscount: itemDiscount(i) > 0,
  }));
  // ── How long the booking actually takes ──────────────────────────────────
  // Summing every item is the right arithmetic for exactly ONE person — their
  // services run back to back — and the wrong arithmetic for anyone else: three
  // friends having a 60-minute service each run ALONGSIDE one another, so the
  // booking takes 60 minutes, not 180. `items` holds EVERY person's legs, so
  // the old sum printed "3 hours" on the details page and blocked out three
  // hours in the customer's calendar / ICS end time.
  //
  // The booking already carries its real span (`startsAt`/`endsAt`, written by
  // the engine — which also covers SEQUENTIAL rounds when there are more people
  // than specialists). `resolveShownDuration` is the SAME rule the booking
  // flow's time step and review step apply, so before and after booking the
  // customer is told one number: the longest single person's chain, replaced by
  // the server's own span once that span is at least as long.
  //
  // One person is unchanged, deliberately: `resolveShownDuration` returns the
  // sum for a single participant, because a solo slot's `endsAt` can include
  // the business's pre/post buffer — time the customer is not in the chair.
  // WHO each item belongs to comes off the item itself (`participantId`), which
  // both the list endpoint and the details endpoint already return — so the list
  // card and the details page reach the same number without the details-only
  // enriched `participants` payload.
  const itemDurationSum = [...services, ...packages].reduce((sum, item) => sum + (item.duration || 0), 0);
  const chainByPerson = new Map();
  for (const i of apt.items ?? []) {
    if (!i?.participantId) continue;
    const mins = Number(i.durationMin ?? i.service?.durationMin ?? i.package?.durationMin ?? 0) || 0;
    chainByPerson.set(i.participantId, (chainByPerson.get(i.participantId) ?? 0) + mins);
  }
  const totalDuration =
    // One person (or items that carry no person at all — legacy/phased rows):
    // the sum IS the answer, and stays byte-for-byte what it was.
    chainByPerson.size > 1
      ? resolveShownDuration({
          parallelMin: Math.max(...chainByPerson.values()),
          participantCount: chainByPerson.size,
          startsAt: apt.startsAt,
          endsAt: apt.endsAt,
        })
      : itemDurationSum;
  const totalPrice = (apt.total != null ? Number(apt.total) : null) ?? (services.reduce((s, i) => s + (i.price || 0), 0) + packages.reduce((s, i) => s + (i.price || 0), 0));
  const subtotal = apt.subtotal != null ? Number(apt.subtotal) : null;
  const discount = apt.discount != null ? Number(apt.discount) : null;
  const items = (apt.items || []).map((i) => ({
    serviceId: i.serviceId || i.service?.id,
    packageId: i.packageId || i.package?.id,
    durationMin: i.durationMin ?? i.service?.durationMin ?? i.duration,
  }));
  // ── Multi-customer GROUP (read-only) ─────────────────────────────────────
  // When the appointment belongs to a group (booked portal-side), the server
  // returns `participants` on the MAIN customer's appointment: each participant
  // with a resolved display `name` (null for unnamed guests → the page renders
  // a localized "Guest N"/"Customer" placeholder) + their own service legs with
  // per-leg times + the resolved specialist name. We pass these through, keeping
  // the raw service `name` + `translations` so the details page can localize via
  // getDisplayNameForLocale exactly like the top-level services. ADDITIVE: when
  // there are no participants (legacy single-customer) we add NO field at all so
  // the existing payload mapping is byte-for-byte unchanged.
  const rawParticipants = Array.isArray(apt.participants) ? apt.participants : [];
  const participants = rawParticipants.length > 0
    ? rawParticipants.map((p) => ({
        id: p.id,
        order: p.order,
        isMain: p.isMain === true,
        name: p.name ?? p.guestName ?? null,
        guestName: p.guestName ?? null,
        // Anonymous guests may carry an optional phone — carry it through so the
        // business can reach them (matches the portal Add-guest flow).
        ...(p.guestPhone ? { guestPhone: p.guestPhone } : {}),
        // A real participant has a resolvable customer id (salonCustomerId or the
        // global customerId); a guest has neither. The component keys the
        // "Customer" vs "Guest N" placeholder off this when there is no name.
        isGuest: !(p.salonCustomerId || p.customerId),
        services: (Array.isArray(p.services) ? p.services : []).map((s) => ({
          serviceId: s.serviceId ?? s.service?.id ?? null,
          name: s.name ?? s.service?.name ?? null,
          translations: s.translations ?? s.service?.translations ?? undefined,
          startsAt: s.startsAt ?? null,
          endsAt: s.endsAt ?? null,
          durationMin: s.durationMin ?? null,
          specialistName: s.specialistName ?? null,
          specialistTranslations: s.specialistTranslations ?? null,
          specialistId: s.specialistId ?? null,
          // Room (resource) this service's slot occupies — surfaced by the server
          // findOne leg. The group section groups participants by
          // `resourceSplitId ?? resourceId` to show who shares a room / together
          // vs separate, plus the room name when the salon exposes it.
          resourceId: s.resourceId ?? null,
          resourceSplitId: s.resourceSplitId ?? null,
          roomName: s.roomName ?? null,
        })),
      }))
    : null;
  return {
    _id: apt.id,
    id: apt.id,
    salonId: apt.salonId || apt.salon?.id,
    salon,
    combinedAppointmentTime: apt.startsAt,
    startsAt: apt.startsAt,
    endsAt: apt.endsAt,
    services,
    packages,
    items,
    totalDuration,
    totalPrice,
    subtotal: subtotal ?? (totalPrice != null && discount != null ? totalPrice + discount : null),
    discount: discount ?? 0,
    discountAmount: apt.discountAmount != null ? Number(apt.discountAmount) : null,
    discountPercent: apt.discountPercent != null ? Number(apt.discountPercent) : null,
    finalPrice: apt.finalPrice != null ? Number(apt.finalPrice) : null,
    tipAmount: apt.tipAmount != null ? Number(apt.tipAmount) : null,
    campaign: apt.campaign ? { id: apt.campaign.id, name: apt.campaign.name } : null,
    specialist,
    specialistId: apt.specialistId || apt.specialist?.id,
    // Phased service: the stages, each with its own window and its own assigned
    // specialist. `specialist` above is only the MAIN phase's — carrying just
    // that is what made the details page tell the customer one name for work
    // genuinely split across people. This curated mapper drops anything it does
    // not list, so the field has to be named explicitly.
    phases: Array.isArray(apt.phases) ? apt.phases : undefined,
    status: (apt.status || '').toLowerCase(),
    specialistMessage: apt.specialistMessage ?? apt.appointmentRequestMessage ?? apt.message ?? null,
    appointmentRequestMessage: apt.appointmentRequestMessage ?? apt.specialistMessage ?? apt.message ?? null,
    customerNote: apt.customerNote ?? null,
    zoomMeetingLink: apt.zoomMeetingLink ?? null,
    // Attendance-confirmation feature: carry the opt-in flag + the two
    // confirmation timestamps through so AppointmentDetails can render the
    // "Confirm coming" prompt / "You confirmed" state. Without these the
    // curated mapper would strip them and the UI would never show.
    confirmAttendanceEnabled: apt.confirmAttendanceEnabled === true || apt.salon?.settings?.confirmAttendanceEnabled === true,
    // Whether the signed-in viewer is the person who MADE this booking. Everyone
    // on a booking may read it; only the booker may cancel or move it. The server
    // resolved this to let the page load at all, so the client never guesses it
    // by comparing ids. This curated mapper drops anything it does not name, so
    // the field has to be listed. Absent (legacy payload) → undefined, which
    // `viewerMayManageBooking` reads as "yes", exactly as before.
    viewerIsBooker: apt.viewerIsBooker,
    attendanceConfirmedAt: apt.attendanceConfirmedAt ?? null,
    attendanceDeclinedAt: apt.attendanceDeclinedAt ?? null,
    confirmationRequestedAt: apt.confirmationRequestedAt ?? null,
    // The customer's own review of this appointment (when COMPLETED + already
    // rated). Lets AppointmentDetails show the "Thanks for your review!" badge
    // instead of the write CTA on reload. Tolerates a couple of server shapes.
    review: apt.review ?? apt.myReview ?? apt.appointmentReview ?? null,
    // Group participants — only present for multi-customer appointments; a
    // single-customer appointment carries no `participants` key at all.
    ...(participants ? { participants } : {}),
  };
};

function unwrapAppointmentsList(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.appointments)) return raw.appointments;
  return [];
}

// —— Biz-scoping for custom domains ——————————————————————————————————————
// On a branded custom domain the customer is the SAME platform user, but the
// account views (home, my-bookings, history) must show ONLY this business's
// data — not the customer's cross-salon activity. These helpers resolve the
// single-tenant salon's slug (once) and filter appointment lists to it.
// In platform mode both are no-ops (domainSalonSlug returns null).
async function domainSalonSlug(dispatch, getState) {
  if (!isDomainMode()) return null;
  const readSlug = () => {
    const d = getState && getState()?.getSalonReducer?.data;
    // The sessionStorage "salonId" bridge is meant to hold a SLUG. Defend
    // against a stale hostname value (a real slug never contains a dot) so a
    // session poisoned before this fix shipped self-heals instead of scoping
    // every list to `salon.slug === "book.stylon.co"` (which matches nothing).
    const bridged =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem("salonId") : null;
    const bridgedSlug = bridged && !bridged.includes(".") ? bridged : null;
    return d?.slug ?? d?.username ?? bridgedSlug;
  };
  let slug = readSlug();
  if (!slug) {
    // Salon not resolved yet (e.g. deep-link straight to /home): resolve by host.
    await dispatch(getSalonSpecificInfo());
    slug = readSlug();
  }
  return slug || null;
}
function scopeToSalonSlug(list, slug) {
  if (!Array.isArray(list)) return list;
  // Fail CLOSED on a custom domain: if the tenant slug couldn't be resolved,
  // show nothing rather than leaking the customer's other-salon appointments
  // onto the branded, single-tenant storefront. Platform mode → no scoping.
  if (!slug) return isDomainMode() ? [] : list;
  return list.filter((a) => (a?.salon?.slug ?? a?.salon?.username) === slug);
}

const getUpcomingAppointments = () => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.getUpcomingAppointments();
        dispatch({ type: "GET_UPCOMING_APPOINTMENTS", payload: data });
        return;
      }
      const raw = await storeApi.getMyAppointments({ status: 'APPROVED' });
      const list = scopeToSalonSlug(unwrapAppointmentsList(raw), await domainSalonSlug(dispatch, getState));
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const upcoming = list
        .filter((a) => a.startsAt && new Date(a.startsAt) >= startOfToday)
        .map(mapAppointmentFromApi)
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
      dispatch({ type: "GET_UPCOMING_APPOINTMENTS", payload: upcoming });
    } catch (error) {
      console.log(error);
      if (isMockMode()) return;
      dispatch({ type: "GET_UPCOMING_APPOINTMENTS", payload: [] });
    }
  };
};

const getPendingAppointments = () => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.getPendingAppointments();
        dispatch({ type: "GET_PENDING_APPOINTMENTS", payload: data });
        return;
      }
      const raw = await storeApi.getMyAppointments({ status: 'PENDING' });
      const list = scopeToSalonSlug(unwrapAppointmentsList(raw), await domainSalonSlug(dispatch, getState));
      dispatch({ type: "GET_PENDING_APPOINTMENTS", payload: list.map(mapAppointmentFromApi) });
    } catch (error) {
      console.log(error);
      if (isMockMode()) return;
      dispatch({ type: "GET_PENDING_APPOINTMENTS", payload: [] });
    }
  };
};

const getHistoryAppointments = () => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.getHistoryAppointments();
        dispatch({ type: "GET_HISTORY_APPOINTMENTS", payload: data });
        return;
      }
      const raw = await storeApi.getMyAppointments();
      const list = scopeToSalonSlug(unwrapAppointmentsList(raw), await domainSalonSlug(dispatch, getState));
      const statusUpper = (s) => (s || '').toUpperCase();
      const history = list
        .filter((a) => {
          const s = statusUpper(a.status);
          return s === 'COMPLETED' || s === 'CANCELLED' || s === 'REJECTED';
        })
        .map(mapAppointmentFromApi);
      dispatch({ type: "GET_HISTORY_APPOINTMENTS", payload: history });
    } catch (error) {
      console.log(error);
      if (isMockMode()) return;
      dispatch({ type: "GET_HISTORY_APPOINTMENTS", payload: [] });
    }
  };
};

const getViewedSalons = () => {
  return async (dispatch) => {
    try {
      // Domain mode: single-tenant — "recently visited salons" is meaningless
      // (there is only this one business), so never surface other salons here.
      if (isDomainMode()) {
        dispatch({ type: "GET_VIEWED_SALONS", payload: [] });
        return;
      }
      if (isMockMode()) {
        const data = await mockApi.getViewedSalons();
        dispatch({ type: "GET_VIEWED_SALONS", payload: data });
        return;
      }
      // Real API: derive "recently visited" from customer's appointments (unique salons)
      const raw = await storeApi.getMyAppointments();
      const all = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
      const bySlug = new Map();
      all.forEach((a) => {
        const salon = a.salon;
        const slug = salon?.slug ?? salon?.username;
        if (slug && salon?.name && !bySlug.has(slug)) {
          bySlug.set(slug, {
            _id: salon.id ?? salon._id,
            name: salon.name,
            username: slug,
            logo: salon.logoUrl ?? salon.logo,
            address: salon.address,
          });
        }
      });
      dispatch({ type: "GET_VIEWED_SALONS", payload: Array.from(bySlug.values()) });
    } catch (error) {
      console.log(error);
      dispatch({ type: "GET_VIEWED_SALONS", payload: [] });
    }
  };
};

/**
 * Load all customer home data with a single API call. Avoids 4–5 duplicate 401s when unauthenticated.
 * Dispatches GET_UPCOMING_APPOINTMENTS, GET_PENDING_APPOINTMENTS, GET_HISTORY_APPOINTMENTS, GET_VIEWED_SALONS.
 */
const loadCustomerHomeData = () => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        await Promise.all([
          dispatch(getUpcomingAppointments()),
          dispatch(getPendingAppointments()),
          dispatch(getHistoryAppointments()),
          dispatch(getViewedSalons()),
        ]);
        return;
      }
      // Domain mode: scope the customer's data to THIS business only (same user,
      // biz-only content). Resolve the tenant slug once, then filter below.
      const scopeSlug = await domainSalonSlug(dispatch, getState);
      const raw = await storeApi.getMyAppointments();
      const list = scopeToSalonSlug(unwrapAppointmentsList(raw), scopeSlug);
      const statusUpper = (s) => (s || '').toUpperCase();
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const upcoming = list
        .filter((a) => statusUpper(a.status) === 'APPROVED' && a.startsAt && new Date(a.startsAt) >= startOfToday)
        .map(mapAppointmentFromApi)
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
      const pending = list
        .filter((a) => statusUpper(a.status) === 'PENDING')
        .map(mapAppointmentFromApi);
      const history = list
        .filter((a) => {
          const s = statusUpper(a.status);
          return s === 'COMPLETED' || s === 'CANCELLED' || s === 'REJECTED';
        })
        .map(mapAppointmentFromApi);

      const bySlug = new Map();
      list.forEach((a) => {
        const salon = a.salon;
        const slug = salon?.slug ?? salon?.username;
        if (slug && salon?.name && !bySlug.has(slug)) {
          bySlug.set(slug, {
            _id: salon.id ?? salon._id,
            name: salon.name,
            username: slug,
            logo: salon.logoUrl ?? salon.logo,
            address: salon.address,
          });
        }
      });

      dispatch({ type: "GET_UPCOMING_APPOINTMENTS", payload: upcoming });
      dispatch({ type: "GET_PENDING_APPOINTMENTS", payload: pending });
      dispatch({ type: "GET_HISTORY_APPOINTMENTS", payload: history });
      // Domain mode: never surface other salons under "recently visited".
      dispatch({ type: "GET_VIEWED_SALONS", payload: scopeSlug ? [] : Array.from(bySlug.values()) });
    } catch (error) {
      console.log(error);
      dispatch({ type: "GET_UPCOMING_APPOINTMENTS", payload: [] });
      dispatch({ type: "GET_PENDING_APPOINTMENTS", payload: [] });
      dispatch({ type: "GET_HISTORY_APPOINTMENTS", payload: [] });
      dispatch({ type: "GET_VIEWED_SALONS", payload: [] });
    }
  };
};

/** In-memory cache for slots, TTL 5 minutes.
 * Key MUST include the inputs that change the server response — serviceIds, packageId, and
 * duration affect the per-service pre/post buffer logic on the server. Without these in the
 * key, a customer who changes service selection and revisits the slots step would see stale
 * slots from the first selection (real bug reported by users). */
const SLOTS_CACHE_TTL_MS = 5 * 60 * 1000;
const slotsCache = new Map();

function slotsCacheKey(salonId, specialistId, specialistIds, date, isEmergency, serviceIds, packageId, duration, serviceSpecialists) {
  const specialistKey =
    specialistId === "any" && Array.isArray(specialistIds) && specialistIds.length > 0
      ? "any:" + [...specialistIds].sort().join(",")
      : String(specialistId ?? "");
  const servicesKey = Array.isArray(serviceIds)
    ? [...serviceIds].map(String).sort().join(",")
    : String(serviceIds ?? "");
  // SP5: fold the per-service picks in so a one-for-all key and a per-service key
  // never collide ('' when absent → existing keys unchanged).
  const serviceSpecialistsKey =
    serviceSpecialists && typeof serviceSpecialists === "object"
      ? Object.entries(serviceSpecialists).map(([k, v]) => `${k}:${v}`).sort().join(",")
      : "";
  return [
    String(salonId ?? ""),
    specialistKey,
    String(date ?? ""),
    isEmergency ? "emergency" : "normal",
    servicesKey,
    String(packageId ?? ""),
    String(duration ?? ""),
    serviceSpecialistsKey,
  ].join("|");
}

/** Test-only helper: wipe the in-memory slots cache between tests. Not part of the public API. */
function __clearSlotsCacheForTests() {
  slotsCache.clear();
}

function getSlotsFromCache(key) {
  const entry = slotsCache.get(key);
  if (!entry || Date.now() > entry.expiresAt) return null;
  return entry.payload;
}

function setSlotsCache(key, payload) {
  slotsCache.set(key, {
    payload,
    expiresAt: Date.now() + SLOTS_CACHE_TTL_MS,
  });
}

/** Merge slots from multiple specialists: dedupe by startsAt, attach specialistIds to each slot. */
function mergeSlotsByStart(slotsPerSpecialist) {
  const byStart = new Map();
  slotsPerSpecialist.forEach(({ specialistId, slots: list }) => {
    const arr = Array.isArray(list) ? list : list?.slots ?? list?.data ?? [];
    arr.forEach((slot) => {
      const start = slot?.startsAt ?? slot?.start ?? slot;
      const key = start ? (typeof start === 'string' ? start : new Date(start).toISOString()) : null;
      if (!key) return;
      if (!byStart.has(key)) {
        byStart.set(key, {
          ...slot,
          startsAt: slot?.startsAt ?? slot?.start,
          endsAt: slot?.endsAt ?? slot?.end,
          specialistIds: [],
        });
      }
      const entry = byStart.get(key);
      if (!entry.specialistIds.includes(specialistId)) {
        entry.specialistIds.push(specialistId);
      }
    });
  });
  return Array.from(byStart.values()).sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
}

/** Build specialistKey for Redux so UI only shows slots when they match current (date, specialist). */
function buildSlotsSpecialistKey(specialistId, specialistIds) {
  if (specialistId === "any" && Array.isArray(specialistIds) && specialistIds.length > 0) {
    return "any:" + [...specialistIds].sort().join(",");
  }
  return String(specialistId ?? "");
}

/** If options.shouldApply is a function, only dispatch GET_SLOTS when it returns true (avoids stale responses overwriting current slots).
 *  If options.returnSlots is true, the thunk resolves with the slots array and does not dispatch (for emergency auto-select). */
const getSlots = (slotsData, options = {}) => {
  const shouldApply = options.shouldApply;
  const returnSlotsOnly = options.returnSlots === true;
  const dispatchSlots = (dispatch, payload) => {
    if (returnSlotsOnly) return;
    if (typeof shouldApply === "function" && !shouldApply()) return;
    dispatch({ type: "GET_SLOTS", payload });
  };
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const specialistKey = buildSlotsSpecialistKey(slotsData?.specialistId, slotsData?.specialistIds);
        dispatchSlots(dispatch, { slots: [], date: slotsData?.date ?? null, specialistKey });
        return returnSlotsOnly ? [] : undefined;
      }
      const raw = slotsData || {};
      const { salonId, specialistId, date, specialistIds } = raw;
      const serviceIds = raw.serviceIds ?? raw.servicesId ?? [];
      const packageId = raw.packageId ?? (Array.isArray(raw.packagesIds) && raw.packagesIds.length > 0 ? raw.packagesIds[0] : null);
      const duration = raw.duration ?? 30;
      const includeOutOfWorkingHours = !!raw.isEmergency;
      const params = { date, duration, serviceIds, packageId, ...(includeOutOfWorkingHours && { includeOutOfWorkingHours: true }) };
      const specialistKey = buildSlotsSpecialistKey(specialistId, specialistIds);

      const cacheKey = slotsCacheKey(
        salonId,
        specialistId,
        specialistIds,
        date,
        includeOutOfWorkingHours,
        serviceIds,
        packageId,
        duration,
        raw.serviceSpecialists,
      );
      const cached = getSlotsFromCache(cacheKey);
      const useCached = cached !== null && !(includeOutOfWorkingHours && Array.isArray(cached) && cached.length === 0);
      if (useCached) {
        console.log('[SLOTS] getSlots from cache', { salonId, specialistId, date, slotsCount: cached.length });
        dispatchSlots(dispatch, { slots: cached, date, specialistKey });
        return returnSlotsOnly ? cached : undefined;
      }

      const t0 = typeof performance !== "undefined" ? performance.now() : 0;
      console.log('[SLOTS] getSlots fetching', { salonId, specialistId, specialistIds, date, duration: params.duration });

      // Phased service: the backend treats the URL specialistId as the main-phase pin.
      // A per-specialist call would 400 if that specialist isn't main-phase-eligible.
      // So for a phased service we must issue a SINGLE call (pinned or 'any') and let
      // the backend resolve phase assignments internally.
      if (raw.hasPhases) {
        const phasedSpecialistId = specialistId || 'any';
        const data = await storeApi.getAvailableSlots(salonId, phasedSpecialistId, params);
        const list = Array.isArray(data) ? data : (data?.slots ?? data?.data ?? []);
        const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
        console.log('[SLOTS] getSlots phased received', { salonId, specialistId: phasedSpecialistId, date, durationMs, slotsCount: list.length });
        setSlotsCache(cacheKey, list);
        dispatchSlots(dispatch, { slots: list, date, specialistKey });
        return returnSlotsOnly ? list : undefined;
      }

      // SP5 multi-service / per-service specialists: the SP4 engine returns
      // appointment start times already accounting for durations, interleaving,
      // ordering constraints and the inter-service wait gap. We render them
      // verbatim — NO client-side recompute / per-specialist merge.
      if (
        raw.serviceSpecialists &&
        Object.keys(raw.serviceSpecialists).length > 0 &&
        Array.isArray(serviceIds) &&
        serviceIds.length > 1
      ) {
        const servicesBody = serviceIds.map((sid) => {
          const pick = raw.serviceSpecialists[String(sid)];
          return pick && pick !== 'any' ? { serviceId: sid, specialistId: pick } : { serviceId: sid };
        });
        const data = await storeApi.getAvailableMultiServiceSlots(salonId, { date, services: servicesBody });
        const list = Array.isArray(data) ? data : (data?.slots ?? data?.data ?? []);
        const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
        console.log('[SLOTS] getSlots multi-service received', { salonId, date, durationMs, slotsCount: list.length });
        setSlotsCache(cacheKey, list);
        dispatchSlots(dispatch, { slots: list, date, specialistKey });
        return returnSlotsOnly ? list : undefined;
      }

      if (specialistId === 'any' && Array.isArray(specialistIds) && specialistIds.length > 0) {
        const results = await Promise.all(
          specialistIds.map((sid) =>
            storeApi.getAvailableSlots(salonId, sid, params).then((data) => ({
              specialistId: sid,
              slots: Array.isArray(data) ? data : data?.slots ?? data?.data ?? [],
            }))
          )
        );
        const list = mergeSlotsByStart(results);
        const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
        console.log('[SLOTS] getSlots merged (any specialist)', { date, durationMs, slotsCount: list.length, perSpecialist: results.map((r) => ({ id: r.specialistId, count: r.slots.length })) });
        setSlotsCache(cacheKey, list);
        dispatchSlots(dispatch, { slots: list, date, specialistKey });
        return returnSlotsOnly ? list : undefined;
      }

      if (!specialistId) {
        dispatchSlots(dispatch, { slots: [], date, specialistKey });
        return returnSlotsOnly ? [] : undefined;
      }

      const data = await storeApi.getAvailableSlots(salonId, specialistId, params);
      const list = Array.isArray(data) ? data : (data?.slots ?? data?.data ?? []);
      const durationMs = typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
      console.log('[SLOTS] getSlots received', { salonId, specialistId, date, durationMs, slotsCount: list.length });
      setSlotsCache(cacheKey, list);
      dispatchSlots(dispatch, { slots: list, date, specialistKey });
      return returnSlotsOnly ? list : undefined;
    } catch (error) {
      console.log('[SLOTS] getSlots error', { error: error?.message ?? error });
      const specialistKey = buildSlotsSpecialistKey(slotsData?.specialistId, slotsData?.specialistIds);
      // `slotsFailed` is the difference between "nobody is free" and "we never
      // found out". Without it the screen showed a dead network as
      // "Fully booked on this date" — a confident falsehood that sends a
      // customer away from a business with an empty diary.
      dispatchSlots(dispatch, {
        slots: [],
        date: slotsData?.date ?? null,
        specialistKey,
        slotsFailed: true,
      });
      console.log(error);
      return returnSlotsOnly ? [] : undefined;
    }
  };
};

const getAppointment = async (_id) => {
  try {
    if (isMockMode()) return null;
    const data = await storeApi.getAppointmentById(_id);
    // Transform API data to match frontend expectations
    return data ? mapAppointmentFromApi(data) : null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const sendBooking = (bookingData) => {
  return async (dispatch) => {
    try {
      if (isMockMode()) {
        dispatch({ type: "SEND_BOOKING", payload: {} });
        return { id: 'mock-appointment-id' };
      }
      const { salonId, startsAt, endsAt, specialistId, services, package: pkg, notes, requirementAnswers, campaignId, discount, discountAmount, discountPercent, participants, roomPreference, coupled, pairings, savedPaymentMethodId, cvv } = bookingData || {};
      const bookingDate = startsAt ? new Date(startsAt).toISOString().slice(0, 10) + 'T00:00:00.000Z' : undefined;
      const body = {
        ...(bookingDate && { bookingDate }),
        startsAt,
        endsAt,
        specialistId,
        notes: notes || undefined,
        services: services?.length ? services.map((s) => {
          if (typeof s === 'string') return { serviceId: s };
          // SP5: forward the per-service specialistId when present (omit otherwise
          // so the engine assigns). serviceId falls back to id/_id for old shapes.
          const serviceId = s.serviceId || s.id || s._id;
          return s.specialistId ? { serviceId, specialistId: s.specialistId } : { serviceId };
        }) : undefined,
        package: pkg ? { packageId: typeof pkg === 'string' ? pkg : (pkg.packageId || pkg.id || pkg._id) } : undefined,
        requirementAnswers: Array.isArray(requirementAnswers) && requirementAnswers.length > 0 ? requirementAnswers : undefined,
        ...(campaignId && { campaignId }),
        ...(discount != null && discount > 0 && { discount: Number(discount) }),
        ...(discountAmount != null && { discountAmount: Number(discountAmount) }),
        ...(discountPercent != null && { discountPercent: Number(discountPercent) }),
        // SP3: forward the group participants (booker + named guests) so the
        // server routes to the multi-customer create. Only a real group (>1).
        ...(Array.isArray(participants) && participants.length > 1 && { participants }),
        // Rooms feature [P2]: seating preference for a 2-participant room-
        // attached group. Only meaningful alongside `participants` — guarded
        // the same way so it can never leak onto a single-customer create.
        ...(Array.isArray(participants) && participants.length > 1
          && (roomPreference === 'singles' || roomPreference === 'double')
          && { roomPreference }),
        // Couples massage: force synchronized start for this 2-person booking.
        // Server create-time enforcement is gated on this flag — must survive
        // this rebuild the same way `participants`/`roomPreference` do.
        ...(coupled === true && { coupled: true }),
        // Multi-person redesign (P4): index-shaped pairings
        // ([{ participantIndices: [i, j] }]) marking which participants must be
        // co-seated (shared room + synchronized start). Forwarded only for a
        // real group (>1 participant) and only when non-empty, so single/legacy
        // bookings stay byte-identical. Legacy `coupled`/`roomPreference` above
        // are still sent during the transition (the server normalizes them into
        // the same internal pairings model). This must survive the body rebuild
        // the same way `participants` does — the thunk destructures a fixed
        // field list, so an un-threaded field is silently dropped.
        ...(Array.isArray(participants) && participants.length > 1
          && Array.isArray(pairings) && pairings.length > 0
          && { pairings }),
        // THE CARD THE CUSTOMER HAS ON FILE RIDES ON WHATEVER BOOKING ELECTED IT.
        //
        // This used to be gated on `coupled === true && participants.length === 2`,
        // mirroring a server gate that had already been deleted: the create's
        // deposit is the SUM OF THE LEGS and has counted no heads since. So a
        // party of three, and any two people who had not ticked "together", were
        // silently booked with no card attached — and a one-person cart of two
        // services could not attach one at all, which is the same defect wearing
        // a smaller number. `savedCardAllowedForThisBooking` now answers the same
        // for every booking; whether a card is attached is decided upstream by
        // which WRITER carries the money (see bookingLegs.depositMechanism),
        // which reads the cart and not the guest list.
        ...(savedCardAllowedForThisBooking() && savedPaymentMethodId
          && { savedPaymentMethodId }),
        ...(savedCardAllowedForThisBooking() && savedPaymentMethodId && cvv
          && { cvv }),
        visitorKey: getVisitorKey(),
      };
      const data = await storeApi.createAppointment(salonId, body);
      dispatch({ type: "SEND_BOOKING", payload: data });
      return data;
    } catch (error) {
      console.log(error);
      // The backend can return `message` as a string, a class-validator array,
      // or a structured object (e.g. { errorCode, message }). Callers do
      // message.toLowerCase()/.includes(), so coerce it to a string here —
      // otherwise the real reason is hidden behind a "toLowerCase is not a
      // function" crash.
      const raw = error.response?.data?.message;
      let message;
      if (Array.isArray(raw)) message = raw.filter(Boolean).join('. ');
      else if (typeof raw === 'string') message = raw;
      else if (raw && typeof raw === 'object') message = raw.message || raw.errorCode || JSON.stringify(raw);
      else message = error.message || 'Booking failed';
      // P4 (final review — Important #2): preserve the machine-readable
      // `errorCode` from the structured error body so callers can branch on it
      // (e.g. PAIRING_UNAVAILABLE) instead of fragile prose substring matching —
      // that covers every server message variant (§4a "…couldn't seat you
      // together…", §4c "…no couple room is free…") + future copy. The code can
      // live either nested under `message` ({ errorCode, message }) or top-level
      // on `data` ({ errorCode }); read both.
      const errorCode =
        (raw && typeof raw === 'object' ? raw.errorCode : undefined) ||
        error.response?.data?.errorCode ||
        undefined;
      // A service or bundle the business no longer offers is refused by name,
      // so the customer is told WHICH one (ITEM_UNAVAILABLE).
      const body = raw && typeof raw === 'object' ? raw : error.response?.data;
      const named = {};
      ['serviceName', 'packageName'].forEach((field) => {
        if (typeof body?.[field] === 'string' && body[field].trim()) named[field] = body[field];
      });
      return { success: false, message, ...(errorCode ? { errorCode } : {}), ...named };
    }
  };
};

const createCustomer = (payload) => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.createCustomer(payload);
        return data.success;
      }
      const salon = getState()?.getSalonReducer?.data;
      const countryCode = getSalonCountryCode(salon);
      const phone = normalizePhoneWithCountryCode(payload.phone, countryCode);
      await storeApi.sendOtp(phone, 'CUSTOMER', getCurrentLocale());
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
};

const loginCustomer = (payload) => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.loginCustomer(payload);
        return data.success;
      }
      const salon = getState()?.getSalonReducer?.data;
      const countryCode = getSalonCountryCode(salon);
      const phone = normalizePhoneWithCountryCode(payload.phone, countryCode);
      await storeApi.sendOtp(phone, 'CUSTOMER', getCurrentLocale());
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
};

const resendOtp = (phone, channel) => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.resendOtp(phone);
        return data.success;
      }
      const salon = getState()?.getSalonReducer?.data;
      const countryCode = getSalonCountryCode(salon);
      const normalized = normalizePhoneWithCountryCode(phone, countryCode);
      // channel='whatsapp' → force WhatsApp (the "Send on WhatsApp" fallback).
      await storeApi.sendOtp(normalized, 'CUSTOMER', getCurrentLocale(), channel);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
};

const verifyOtp = (payload) => {
  return async (dispatch, getState) => {
    try {
      if (isMockMode()) {
        const data = await mockApi.verifyOtp(payload);
        if (data.success) {
          localStorage.setItem("customerId", data.id || 'mock-user-id');
          localStorage.setItem("customer", JSON.stringify({ id: data.id || 'mock-user-id', name: 'Mock User' }));
          localStorage.setItem("authToken", "mock-token");
          dispatch({ type: "LOGIN" });
          return { customerId: data.id || 'mock-user-id', needsOnboarding: false };
        }
        return null;
      }
      const salon = getState()?.getSalonReducer?.data;
      const countryCode = getSalonCountryCode(salon);
      const normalizedPhone = normalizePhoneWithCountryCode(payload.phone, countryCode);
      // Carry the current locale + explicit flag into verify so the backend can
      // persist the customer's language at account creation. A device-default
      // locale must NOT be marked explicit (so a salon "force language" can
      // still override it).
      const res = await storeApi.verifyOtp(normalizedPhone, payload.otp, {
        locale: getCurrentLocale(),
        localeExplicit: storeApi.isLanguageExplicit(),
      });
      const accessToken = res?.accessToken ?? res?.access_token;
      const onboardingToken = res?.onboardingToken ?? res?.onboarding_token;
      const hasOnboardingOnly = onboardingToken && !accessToken;
      const token = accessToken || onboardingToken;
      const user = res?.user || {};
      const customerId = user?.id ?? res?.context?.customer?.id;

      if (hasOnboardingOnly) {
        try {
          if (token) localStorage.setItem("authToken", token);
          sessionStorage.setItem("onboardingPhone", normalizedPhone);
        } catch (e) {
          console.warn("Storage write failed (e.g. private mode)", e);
        }
        return { customerId, needsOnboarding: true };
      }

      // Full login: need a valid token so protected routes and API calls work
      if (!token || String(token) === "undefined") {
        console.warn("Verify OTP: no access token in response", res?.tokenType);
        return { customerId, needsOnboarding: true };
      }

      const customer = res?.context?.customer;
      // Store minimal fields; include photoUrl only when it's a URL (not base64) so avatar shows in web store
      const customerPayload = {
        id: customer?.id ?? customerId,
        _id: customer?.id ?? customerId,
        name: customer?.fullName ?? customer?.name ?? user?.displayName ?? user?.phone ?? "Customer",
        fullName: customer?.fullName ?? customer?.name,
        ...(customer?.phone && { phone: customer.phone }),
        ...(customer?.photoUrl && typeof customer.photoUrl === "string" && !customer.photoUrl.startsWith("data:") && { photoUrl: customer.photoUrl }),
        ...(customer?.image && typeof customer.image === "string" && !customer.image.startsWith("data:") && !customer?.photoUrl && { photoUrl: customer.image }),
      };
      localStorage.setItem("authToken", token);
      if (accessToken) localStorage.setItem("accessToken", accessToken);
      // Persist the refresh token so the API interceptor can renew the
      // access token silently on 401 instead of bouncing the customer to
      // the login page. Server returns it as `refreshToken`.
      if (res?.refreshToken) localStorage.setItem("refreshToken", res.refreshToken);
      if (customerId) localStorage.setItem("customerId", customerId);
      try {
        localStorage.setItem("customer", JSON.stringify(customerPayload));
      } catch (e) {
        console.warn("Could not stringify customer for storage", e);
      }
      // Sync locale to server (after storage is set so interceptor sends the token).
      // Pass the explicit flag so a login that only carried a device default
      // does NOT mark the language as the customer's real choice.
      const locale = getCurrentLocale();
      if (['en', 'ar', 'he'].includes(locale)) {
        storeApi.updateUserLocale(locale, storeApi.isLanguageExplicit()).catch(() => {});
      }
      dispatch({ type: "LOGIN" });
      return { customerId, needsOnboarding: false };
    } catch (error) {
      console.log(error);
      // A refusal the server answered ("Invalid OTP", "OTP expired", 429) goes
      // on to the screen that asked, which says it in the shopper's language
      // (utils/loginRefusalMessage.js). Swallowed here, every wrong code read
      // "failed to verify". No answer at all is still null.
      if (error?.response) throw error;
      return null;
    }
  };
};

/**
 * `options.onRefusal(error)` hears the server's refusal before this answers
 * `false` — the only way a caller can tell WHY. The sign-up modal needs it for
 * `GENDER_REQUIRED` (a business that switched to requiring gender while the
 * page was open). Every existing caller passes nothing and sees no change.
 */
const onboardCustomerComplete = (payload, options = {}) => {
  return async (dispatch) => {
    try {
      if (isMockMode()) {
        localStorage.setItem("customerId", "mock-user-id");
        localStorage.setItem("customer", JSON.stringify({ id: "mock-user-id", name: payload.fullName || "Mock User" }));
        localStorage.setItem("authToken", "mock-token");
        dispatch({ type: "LOGIN" });
        return true;
      }
      const phone = sessionStorage.getItem("onboardingPhone");
      const body = { ...payload, ...(phone ? { phone } : {}) };
      const res = await storeApi.onboardCustomer(body);
      const token = res.accessToken || res.token;
      const user = res.user || {};
      const customer = res.customer || res.context?.customer;
      const customerId = customer?.id ?? user.id;
      // Store minimal fields; include photoUrl when it's a URL so avatar shows in web store
      const customerPayload = {
        id: customerId,
        _id: customerId,
        name: customer?.fullName ?? user?.displayName ?? payload?.fullName ?? "Customer",
        fullName: customer?.fullName ?? user?.displayName ?? payload?.fullName ?? "Customer",
        ...(customer?.phone && { phone: customer.phone }),
        ...(customer?.photoUrl && typeof customer.photoUrl === "string" && !customer.photoUrl.startsWith("data:") && { photoUrl: customer.photoUrl }),
        ...(customer?.image && typeof customer.image === "string" && !customer.image.startsWith("data:") && !customer.photoUrl && { photoUrl: customer.image }),
      };
      if (token) localStorage.setItem("authToken", token);
      if (res.accessToken) localStorage.setItem("accessToken", res.accessToken);
      // Persist refresh token (see verifyOtp comment for rationale).
      if (res?.refreshToken) localStorage.setItem("refreshToken", res.refreshToken);
      localStorage.setItem("customerId", customerId);
      localStorage.setItem("customer", JSON.stringify(customerPayload));
      sessionStorage.removeItem("onboardingPhone");
      try { sessionStorage.removeItem("registrationData"); } catch (e) {}
      // Sync locale to server so WhatsApp notifications use correct language.
      // Pass the explicit flag so an onboarding that only carried a device
      // default does NOT mark the language as the customer's real choice.
      const locale = getCurrentLocale();
      if (['en', 'ar', 'he'].includes(locale)) {
        storeApi.updateUserLocale(locale, storeApi.isLanguageExplicit()).catch(() => {});
      }
      dispatch({ type: "LOGIN" });
      return true;
    } catch (error) {
      console.log(error);
      try { if (options && typeof options.onRefusal === "function") options.onRefusal(error); } catch (e) {}
      return false;
    }
  };
};

/** A refusal's sentence, as a string, whichever shape the server sent. */
function refusalWords(error) {
  const data = error?.response?.data;
  const said = data?.message;
  if (typeof said === 'string' && said) return said;
  if (said && typeof said === 'object' && typeof said.message === 'string' && said.message) return said.message;
  if (Array.isArray(said) && said.length) return said.filter((x) => typeof x === 'string').join('; ');
  return typeof error?.message === 'string' ? error.message : '';
}

/** A refusal's code, flat or nested under `message`. */
function refusalCode(error) {
  const data = error?.response?.data;
  if (typeof data?.errorCode === 'string') return data.errorCode;
  if (typeof data?.message?.errorCode === 'string') return data.message.errorCode;
  return null;
}

const cancelAppointment = (appointmentId, reason) => {
  return async (dispatch) => {
    try {
      if (isMockMode()) {
        dispatch(loadCustomerHomeData());
        return { success: true };
      }
      await storeApi.cancelAppointment(appointmentId, reason ? { reason } : {});
      dispatch(loadCustomerHomeData());
      return { success: true };
    } catch (error) {
      console.log(error);
      // The server's refusal is `{ message: { errorCode, message } }` when it
      // is structured (APPOINTMENT_ALREADY_SETTLED when the auto-complete cron
      // wins the race). Handing that OBJECT to the toast rendered it as a React
      // child and threw; the page's `msg.includes` threw too (SWP-F-18). Words
      // only, from wherever the server put them.
      const message = refusalWords(error) || 'Cancel failed';
      toast.error(message);
      return { success: false, error, message, errorCode: refusalCode(error) };
    }
  };
};

const getUser = () => {
  return async () => {
    try {
      if (isMockMode()) {
        const data = await mockApi.getUser();
        if (data) {
          localStorage.setItem("customerId", data.id || 'mock-user-id');
          localStorage.setItem("customer", JSON.stringify(data));
        }
        return data?.id || 'mock-user-id';
      }
      const data = await storeApi.getMe();
      const customerId = data.id;
      const ctx = data.context?.customer;
      const customer = {
        id: ctx?.id ?? customerId,
        name: ctx?.fullName ?? ctx?.name ?? data.displayName ?? "Customer",
        ...(ctx?.phone && { phone: ctx.phone }),
      };
      localStorage.setItem("customerId", customerId);
      localStorage.setItem("customer", JSON.stringify(customer));
      return customerId;
    } catch (error) {
      console.log(error);
      return null;
    }
  };
};

// Delegates to the canonical getVisitorKey() so page views, campaign clicks, ad
// clicks, and bookings/orders all share one stable visitor id.
const getOrCreateAnonymousId = () => getVisitorKey();

/**
 * Fire-and-forget: if the visitor landed from one of our ads, the landing URL
 * carries stcid (our click id) + stad/stcmp/stsid (ad/campaign/salon ids). Capture
 * it once so a later booking/order can be attributed to the ad. Never blocks.
 */
const captureAdClickFromUrl = () => {
  try {
    if (isMockMode() || typeof window === "undefined") return;
    const q = new URLSearchParams(window.location.search);
    const clickId = q.get("stcid");
    const salonId = q.get("stsid");
    if (!clickId || !salonId) return;
    fetch(`${BASE_URL}/ads/click`, {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clickId,
        salonId,
        adId: q.get("stad") || undefined,
        campaignId: q.get("stcmp") || undefined,
        visitorKey: getVisitorKey(),
        platformClickId:
          q.get("fbclid") || q.get("gclid") || q.get("ttclid") || undefined,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch (_err) {}
};

/**
 * Build page-view request headers. When a customer is logged in, attach their
 * Bearer token so the server (OptionalJwtAuthGuard) can stamp the view with their
 * User.id for Messages page-view affinity. Anonymous visitors get no header and the
 * endpoint still records the view as before. Token key is 'authToken' (see
 * AuthContext / verifyOtp).
 */
function buildPageViewHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token =
      typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (token && token !== 'undefined') {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (_err) {}
  return headers;
}

/** Fire-and-forget: record store page view. Never blocks main flow. */
const viewSalon = (salonSlug) => {
  return () => {
    if (isMockMode() || !salonSlug) return;
    try {
      captureAdClickFromUrl();
      const url = `${BASE_URL}/config/salon-by-slug/${encodeURIComponent(salonSlug)}/view`;
      const visitorKey = getOrCreateAnonymousId();
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: buildPageViewHeaders(),
        body: JSON.stringify({ visitorKey, eventType: 'page_view' }),
        keepalive: true,
      }).catch(() => {});
    } catch (_err) {}
  };
};

/**
 * Fire-and-forget: record booking flow entry. Never blocks main flow.
 *
 * `listing` NAMES WHICH LISTING THE FLOW STARTED FROM (2026-10-11), when the caller knows one: a
 * service from a "book this service" card, a package from its detail page, a deep link's
 * ?service=/?package=. The brain's service and package judges (ListingJudge) key their
 * view-to-booking-start comparisons on this the way the product funnel keys on productId — without
 * it a booking flow entry says a booking started, never for WHICH service or package.
 */
const recordBookingFlowEntry = (salonSlug, listing = {}) => {
  return () => {
    if (isMockMode() || !salonSlug) return;
    try {
      const url = `${BASE_URL}/config/salon-by-slug/${encodeURIComponent(salonSlug)}/view`;
      const visitorKey = getOrCreateAnonymousId();
      const { serviceId, packageId } = listing || {};
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: buildPageViewHeaders(),
        body: JSON.stringify({
          visitorKey,
          eventType: 'booking_flow_entry',
          ...(serviceId && { serviceId }),
          ...(packageId && { packageId }),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_err) {}
  };
};

const log = async (eventName, extraData = {}, salonUsername = null) => {
  if (typeof console !== 'undefined' && console.log) {
    console.log('[Stylon]', eventName, { ...extraData, salonUsername });
  }
  if (isMockMode()) {
    extraData['isMobile'] = isMobile;
    extraData['browserName'] = browserName;
    extraData['deviceType'] = deviceType;
    extraData['salonUsername'] = salonUsername;
    // Old backend had /customer-web-log; real API can add logging later
  }
};

export { buildSlotsSpecialistKey, slotsCacheKey, __clearSlotsCacheForTests };

export {
  getSalonInfo,
  getSalonCountryCode,
  getSlots,
  sendBooking,
  getSalonSpecificInfo,
  getSalonSpecificInfoByHost,
  createCustomer,
  loginCustomer,
  verifyOtp,
  onboardCustomerComplete,
  resendOtp,
  viewSalon,
  recordBookingFlowEntry,
  getUser,
  getUpcomingAppointments,
  getHistoryAppointments,
  getViewedSalons,
  loadCustomerHomeData,
  cancelAppointment,
  getPendingAppointments,
  getAppointment,
  log,
};
